import { Entry } from "contentful";
import Head from "next/head";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import type { PaginationMeta } from "~/types/pagination";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import { buildPaginationUrl } from "~/utils/pagination-utils";
import textUtils from "~/utils/text-utils";

type Props = {
  entry: Entry<Editorials.SeoMetadata>;
  titleSuffix?: string;
  titleOverride?: string;
  productDetail?: CrossCountry.DetailedProduct;
  selected?: CrossCountry.DetailedProduct;
  canonical?: string;
  lastProductCategory?: string;
  hreflangs?: { locale: string; url: string }[];
  pagination?: PaginationMeta;
};

export default function SeoHead(props: Props) {
  const router = useRouter();
  const t = useTranslations();

  const noIndex = props.entry.fields?.no_index;
  const noFollow = props.entry.fields?.no_follow;
  const localizedSeoTitleCountries = process.env.NEXT_PUBLIC_ENABLE_LOCALIZED_SEO_TITLE_COUNTRIES?.split(",") ?? [];
  const enableLocalizedSeoTitle = router.locale && localizedSeoTitleCountries.includes(router.locale);
  const isGenericPdp = props.entry.fields.name === Constants.GENERIC_PDP_SEO;
  const selected = props.selected;

  const robots = useMemo(() => {
    if (noIndex && noFollow) {
      return "none";
    } else if (noIndex) {
      return "noindex";
    } else if (noFollow) {
      return "nofollow";
    } else {
      return "all";
    }
  }, [noIndex, noFollow]);

  if (!isActiveEntry(props.entry)) return null;

  const keywords = Array.isArray(props.entry.fields.keywords)
    ? props.entry.fields.keywords.join(", ")
    : props.entry.fields.keywords;

  const getTitle = (product?: CrossCountry.DetailedProduct) => {
    const titleSuffix = props.entry.fields.noTitleSuffix ? "" : ` | ${props.titleSuffix}`;

    if (product) {
      /*
        PDP title in order of priority
        1. SEO Metadata from Contentful (no "generic_pdp_seo") + suffix
        2. Localized PRODUCT CATEGORY (the last level of product category) – Name Product Details – COLOR VARIANT | KIKO
          a. Last level of product category: product_class_lev3_desc (Lipstick)
          b. Name Product Details: display_value (without the last number “Unlimited Double Touch 103”)
          c. shade_number (“06”) + localized color (“Pearly Anthracite”)
          d. Suffix: | KIKO"
        3. Attribute marketing_title
        4. Attribute lastProductCategory + display_value + (color || "") + suffix
        5. Attribute display_value + suffix
      */
      if (props.entry.fields.seoTitle && !isGenericPdp) {
        return props.entry.fields.seoTitle + titleSuffix;
      }

      const productName = product?.display_value;
      let color = "";
      if (selected?.shade_number && selected.color) {
        color = ` - ${selected.shade_number} ${selected.color}`;
      } else if (selected?.color) {
        color = ` - ${props.selected?.color}`;
      }

      if (props.lastProductCategory && enableLocalizedSeoTitle) {
        return `${props.lastProductCategory} - ${productName}${color}${titleSuffix}`;
      } else if (product.marketing_title) {
        return product.marketing_title;
      } else if (product.display_value) {
        return `${product.display_value}${titleSuffix}`;
      }
    } else {
      return (props.titleOverride || props.entry.fields.seoTitle) + titleSuffix;
    }
  };

  // Pagination meta tags helper
  const getPaginationTags = () => {
    if (!props.pagination) return null;

    const { currentPage, totalPages, baseUrl, preserveFilters } = props.pagination;
    const absoluteBaseUrl = `https://www.kikocosmetics.com${baseUrl}`;

    // Build prev/next URLs (absolute for SEO)
    const prevPage = currentPage > 1 ? currentPage - 1 : undefined;
    const nextPage = currentPage < totalPages ? currentPage + 1 : undefined;

    const prevUrl = prevPage ? buildPaginationUrl(absoluteBaseUrl, prevPage, preserveFilters) : undefined;
    const nextUrl = nextPage ? buildPaginationUrl(absoluteBaseUrl, nextPage, preserveFilters) : undefined;

    return (
      <>
        {prevUrl && <link rel="prev" href={prevUrl} />}
        {nextUrl && <link rel="next" href={nextUrl} />}
      </>
    );
  };

  // Update hreflang URLs to include page parameter if paginated
  const getHreflangs = () => {
    if (!props.hreflangs) return null;

    return props.hreflangs.map((hreflang, index) => {
      let url = hreflang.url;

      // Add page parameter to hreflang if paginated (page > 1)
      if (props.pagination && props.pagination.currentPage > 1) {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}page=${props.pagination.currentPage}`;
      }

      return <link key={index} rel="alternate" hrefLang={hreflang.locale} href={url} />;
    });
  };

  const getDescription = (product?: CrossCountry.DetailedProduct) => {
    if (product) {
      /*
        PDP title in order of priority
        1. Color Variant (Shade Number + Color Description) + ": " + localized short_description value from the Parent Product (root)
        2. Attribute marketing_description
        3. If neither of these is present, the fallback is the description field of the SeoMetadata component.

        Always append:
          * a "."
          * the localized generic.buyNow
      */

      if (product?.short_description) {
        const escapedShortDesc = textUtils.escapeHtmlParagraph(product.short_description);

        if (selected?.shade_number && selected?.color) {
          return `${selected.shade_number} ${selected.color}: ${escapedShortDesc}`;
        } else if (selected?.color) {
          return `${selected?.color}: ${escapedShortDesc}`;
        }
        return escapedShortDesc;
      } else if (product.marketing_description) {
        return product.marketing_description;
      }
    }

    return props.entry.fields.description;
  };

  return (
    <Head key={router.asPath}>
      <title>{getTitle(props.productDetail)}</title>
      <meta name="description" content={`${getDescription(props.productDetail)}. ${t("generic.buyNow")}`} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      {props.canonical && <link rel="canonical" href={props.canonical} />}
      {getPaginationTags()}
      {getHreflangs()}
    </Head>
  );
}
